import React, { useMemo } from 'react';
import type { PriceWidgetInstanceConfig, PriceData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatFloat, formatVolume, buildTimeAxisFormatter } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { LineChart, ChartLegend, useChartLegend } from '../../../blocks/charts';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';
import { DEFAULT_COLORS } from '../../../design';
import { useAuthStore } from '../../../../../store/authStore';
import type { ThemeMode } from '../../../../../types';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: PriceData = {
  ticker: 'SAMPLE',
  latest_price: 187.44,
  latest_volume: 52341200,
  adtv_3m: 61234500,
  price_history: [
    { date: '2025-01-02', price: 172.10, volume: 41200000, adtv: 58000000 },
    { date: '2025-01-15', price: 175.30, volume: 45100000, adtv: 58500000 },
    { date: '2025-01-31', price: 178.90, volume: 52000000, adtv: 59000000 },
    { date: '2025-02-14', price: 176.20, volume: 38900000, adtv: 59200000 },
    { date: '2025-02-28', price: 181.50, volume: 47300000, adtv: 59800000 },
    { date: '2025-03-07', price: 184.80, volume: 50200000, adtv: 60500000 },
    { date: '2025-03-14', price: 183.10, volume: 44800000, adtv: 60800000 },
    { date: '2025-03-21', price: 187.44, volume: 52341200, adtv: 61234500 },
  ],
  index_ticker: null,
  index_price_history: null,
};

interface PriceWidgetRendererProps {
  config: PriceWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const PriceWidgetRenderer: React.FC<PriceWidgetRendererProps> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<PriceData>(
    'price',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  // Merge price + optional index data for the chart
  const chartData = useMemo(() => {
    if (!displayData?.price_history) return [];
    return displayData.price_history.map((row) => {
      const point: Record<string, unknown> = {
        date: row.date,
        price: row.price,
        volume: row.volume,
      };
      if (displayData.index_price_history) {
        const match = displayData.index_price_history.find((ip) => ip.date === row.date);
        if (match) point.indexPrice = match.price;
      }
      return point;
    });
  }, [displayData]);

  const hasIndex = !!(displayData?.index_ticker && displayData?.index_price_history);

  const themeMode: ThemeMode = useAuthStore((s) => s.user?.theme) ?? 'light';
  const { hiddenKeys, toggle, toggleAll } = useChartLegend();

  // Build series definitions for the chart block
  const series = useMemo<LineChartSeries[]>(() => {
    const result: LineChartSeries[] = [
      {
        yKey: 'price',
        yName: displayData?.ticker || 'Price',
        markers: false,
      },
    ];
    if (hasIndex) {
      result.push({
        yKey: 'indexPrice',
        yName: displayData?.index_ticker || 'Index',
        markers: false,
      });
    }
    return result;
  }, [displayData, hasIndex]);

  // Time-aware X axis formatter
  const dates = useMemo(() => chartData.map((d) => d.date as string), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);

  // Legend series
  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const lastRow = chartData.length > 0 ? chartData[chartData.length - 1] : null;
    return series.map((s, i) => ({
      key: s.yKey,
      name: s.yName,
      color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      value: lastRow ? lastRow[s.yKey] as number : null,
    }));
  }, [series, chartData]);
  const allKeys = useMemo(() => series.map((s) => s.yKey), [series]);

  if (loading) {
    return <WidgetLoadingState size="sm" />;
  }

  if (error) {
    return <WidgetErrorState message={error} onRetry={refetch} />;
  }

  if (!displayData) return null;

  return (
    <div className={`h-full flex flex-col overflow-hidden bg-surface ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}

      {/* Headline metrics */}
      <div className="flex items-center gap-4 px-3 py-1 border-b border-neutral-100 shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-neutral-500 uppercase">Price</span>
          <span className="text-sm font-semibold text-neutral-800">
            {formatFloat(displayData.latest_price)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-neutral-500 uppercase">Vol</span>
          <span className="text-sm text-neutral-700">
            {formatVolume(displayData.latest_volume)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-neutral-500 uppercase">ADTV 3M</span>
          <span className="text-sm text-neutral-700">
            {formatVolume(displayData.adtv_3m)}
          </span>
        </div>
        {displayData.index_ticker && (
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs font-medium text-neutral-500 uppercase">vs</span>
            <span className="text-sm text-secondary-600 font-medium">
              {displayData.index_ticker}
            </span>
          </div>
        )}
      </div>

      {/* Price chart */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {chartData.length > 0 ? (
          <LineChart
            data={chartData}
            xKey="date"
            series={series}
            legend={false}
            hiddenSeries={hiddenKeys}
            showEndValueTags
            xLabelFormatter={timeFormatter}
            theme={themeMode}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-neutral-500">
            No price history available
          </div>
        )}

        {/* Floating draggable legend */}
        <div className="absolute top-2 left-12 z-10">
          <ChartLegend
            series={legendSeries}
            hiddenKeys={hiddenKeys}
            onToggle={toggle}
            onToggleAll={(show) => toggleAll(show, allKeys)}
            defaultCollapsed
          />
        </div>
      </div>
    </div>
  );
};

export default PriceWidgetRenderer;
